// ============================================================
// agentCommandQueue — read-only view of the canonical agent approval
// queue (sq_agent_action_requests) for the Operations Agent Command
// workroom.
//
// Reuses the existing Agent Workspace API verbatim: lists the operator's
// agent workspaces, then the action requests inside each. NO new table,
// NO new endpoint. Approving/rejecting a request stays on the Agent
// Workspace surface — this is a read-only projection, never a second
// gate.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, isAuthError } from '../../../../api/client'

export interface ApprovalQueueRow {
  id: string
  workspace_id: string
  title: string
  action_type: string
  risk_tier: string
  approval_status: string
  execution_status: string
  prepared_by_agent_id: string | null
  created_at: string
}

export interface ApprovalQueueState {
  rows: ApprovalQueueRow[]
  loading: boolean
  error: string | null
  authRequired: boolean
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) return !err.message || err.message === err.code ? err.code : `${err.code}: ${err.message}`
  if (err instanceof Error) return err.message || 'request_failed'
  return String(err)
}

function normalizeRow(row: Record<string, unknown>): ApprovalQueueRow {
  return {
    id: String(row.id ?? ''),
    workspace_id: String(row.workspace_id ?? ''),
    title: String(row.title ?? 'Untitled request'),
    action_type: String(row.action_type ?? ''),
    risk_tier: String(row.risk_tier ?? 'medium'),
    approval_status: String(row.approval_status ?? 'pending_approval'),
    execution_status: String(row.execution_status ?? 'not_started'),
    prepared_by_agent_id: typeof row.prepared_by_agent_id === 'string' ? row.prepared_by_agent_id : null,
    created_at: String(row.created_at ?? ''),
  }
}

export function useOperationsApprovalQueue() {
  const [state, setState] = useState<ApprovalQueueState>({ rows: [], loading: true, error: null, authRequired: false })

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const { workspaces } = await api.agentWorkspace.workspaces.list()
      const perWorkspace = await Promise.all(
        (workspaces || []).map((w) =>
          api.agentWorkspace.actionRequests.list(w.id).then((r) => r.action_requests || []),
        ),
      )
      const rows = perWorkspace
        .flat()
        .map(normalizeRow)
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
      setState({ rows, loading: false, error: null, authRequired: false })
    } catch (err) {
      if (isAuthError(err)) setState({ rows: [], loading: false, error: null, authRequired: true })
      else setState({ rows: [], loading: false, error: formatError(err), authRequired: false })
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return { ...state, reload: load }
}
